import { useAuth } from '../lib/auth-context';
import { getUsageRows, type UsageRow } from '../lib/api';
import useSWR from 'swr';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function UsagePage() {
  const { user, session } = useAuth();
  const { data, error } = useSWR(
    user && session ? ['usage', user.id] : null,
    async () => {
      const response = await getUsageRows(session!.access_token);
      const body = (await response.json().catch(() => null)) as { usage?: UsageRow[]; error?: string } | null;

      if (!response.ok) {
        throw new Error(body?.error ?? 'Failed to load usage data.');
      }

      return body?.usage ?? [];
    },
  );

  if (error) return <div className="text-red-600">Failed to load usage data.</div>;
  if (!data) return <div className="text-gray-500">Loading usage data...</div>;

  // Aggregate by date for chart
  const byDate = data.reduce<Record<string, number>>((acc, row) => {
    acc[row.usage_date] = (acc[row.usage_date] ?? 0) + row.request_count;
    return acc;
  }, {});
  const chartData = Object.entries(byDate)
    .map(([date, requests]) => ({ date, requests }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Usage</h2>

      {chartData.length > 0 ? (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Daily Requests (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={12} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="requests" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-gray-500">No usage data yet. Start using the MCP tools to see usage here.</p>
      )}

      {data.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Tool</th>
                <th className="text-right px-4 py-3">Requests</th>
                <th className="text-right px-4 py-3">Cache Hits</th>
                <th className="text-right px-4 py-3">Errors</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-3">{row.usage_date}</td>
                  <td className="px-4 py-3">{row.tool_name}</td>
                  <td className="px-4 py-3 text-right">{row.request_count}</td>
                  <td className="px-4 py-3 text-right">{row.cache_hit_count}</td>
                  <td className="px-4 py-3 text-right">{row.error_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
